import gsap from 'gsap';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import { Icon } from '@column/column-ui-kit';

import { ImageViewer } from '~/components/ImageCapture/ImageViewer';
import { DownloadIcon } from '~/elements';

const QuickLookBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0 0 0 / 40%);
  opacity: 0;
  z-index: 10;
  display: grid;
  place-items: center;
  padding: 24px;
`;

const QuickLookImageContainer = styled.div`
  position: relative;
  border-radius: 8px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
  z-index: 11;
  width: 100%;
  height: auto;
  max-width: min(960px, 90vw);
  max-height: 90vh;
  will-change: transform, opacity;
`;

const ClickableThumb = styled.div`
  max-width: 420px;
  cursor: pointer;
  transition: box-shadow 0.2s;
  border-radius: 8px;
  box-shadow: 0 0 0 1px ${({ theme }) => theme.secondary.blendToBackground(200)};
  position: relative;
  margin-left: 16px;

  &:hover {
    box-shadow:
      0 0 0 1px ${({ theme }) => theme.primary.background},
      0 1px 3px rgba(0, 0, 0, 0.3);
  }
`;

const ThumbPlaceholder = styled.div<{ $isHidden?: boolean }>`
  position: relative;
  border-radius: 8px;
  overflow: hidden;

  img,
  canvas {
    transition: opacity 0.2s ease;
    opacity: ${({ $isHidden }) => ($isHidden ? 0 : 1)};
  }
`;

const HoverZoom = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0.9);
  z-index: 2;
  width: 32px;
  height: 32px;
  border-radius: 9999px;
  background: rgba(0 0 0 / 40%);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  backdrop-filter: blur(4px);
  transition:
    opacity 0.16s ease,
    transform 0.16s ease;

  svg {
    --icon-size: 16px;
    --icon-color: #ffffff;
  }

  ${ClickableThumb}:hover & {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`;

const DownloadImageButton = styled.button<{ $isVisible: boolean }>`
  position: absolute;
  bottom: 24px;
  left: 50%;
  border: none;
  outline: none;
  cursor: pointer;
  padding: 8px 16px 8px 12px;
  border-radius: 8px;
  background-color: rgba(0 0 0 / 60%);
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  color: #fff;
  backdrop-filter: blur(${({ $isVisible }) => ($isVisible ? '4px' : '0px')});
  box-shadow: 0 0 0 1px rgba(0 0 0 / 20%) inset;
  transition:
    background-color 0.2s,
    opacity 0.2s ease,
    transform 0.2s ease,
    backdrop-filter 0.2s ease;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transform: translateX(-50%) scale(${({ $isVisible }) => ($isVisible ? '1' : '0.95')});

  &:hover {
    background-color: rgba(0 0 0 / 80%);
  }

  svg {
    --icon-size: 16px;
    --icon-color: #fff;
  }
`;

export const QuickLookImage: React.FC<{ src: string; errorMessage?: string; fileName?: string }> = ({
  src,
  fileName,
}) => {
  const thumbRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const originRectRef = useRef<DOMRect | null>(null);
  const [showPlaceholder, setShowPlaceholder] = useState(false);
  const [showDownloadButton, setShowDownloadButton] = useState(false);

  const open = useCallback(() => {
    if (!src) return;
    if (thumbRef.current) {
      originRectRef.current = thumbRef.current.getBoundingClientRect();
    }
    setShowPlaceholder(true);
    setViewerReady(false);
    setIsOpen(true);
    // Delay showing the download button to allow initial hidden state to render
    setTimeout(() => setShowDownloadButton(true), 10);
  }, [src]);

  const overlayRef = useRef<HTMLDivElement>(null);
  const animContainerRef = useRef<HTMLDivElement>(null);
  const downloadBtnRef = useRef<HTMLButtonElement>(null);
  const [viewerReady, setViewerReady] = useState(false);

  useEffect(() => {
    if (!isOpen || !viewerReady) return;

    const overlay = overlayRef.current;
    const animEl = animContainerRef.current;
    if (!overlay || !animEl) return;

    const finalRect = animEl.getBoundingClientRect();
    const originRect = originRectRef.current;

    gsap.set(overlay, { opacity: 0 });

    if (originRect) {
      const originCX = originRect.left + originRect.width / 2;
      const originCY = originRect.top + originRect.height / 2;
      const finalCX = finalRect.left + finalRect.width / 2;
      const finalCY = finalRect.top + finalRect.height / 2;
      const dx = originCX - finalCX;
      const dy = originCY - finalCY;
      const scaleX = originRect.width / finalRect.width;
      const scaleY = originRect.height / finalRect.height;

      gsap.set(animEl, { opacity: 1, x: dx, y: dy, scaleX, scaleY, transformOrigin: 'center center' });

      const tl = gsap.timeline();
      tl.to(overlay, { opacity: 1, duration: 0.18, ease: 'power2.out' }, 0)
        .to(animEl, { x: 0, y: 0, scaleX: 1, scaleY: 1, duration: 0.2, ease: 'power2.out' }, 0)
        .play();
    } else {
      gsap.set(animEl, { opacity: 0, scale: 0.96 });
      const tl = gsap.timeline();
      tl.to(overlay, { opacity: 1, duration: 0.2, ease: 'power2.out' }, 0)
        .to(animEl, { opacity: 1, scale: 1, duration: 0.2, ease: 'power2.out' }, 0)
        .play();
    }
  }, [isOpen, viewerReady]);

  const handleClose = useCallback(() => {
    const overlay = overlayRef.current;
    const animEl = animContainerRef.current;
    if (!overlay || !animEl) {
      setIsOpen(false);
      setShowDownloadButton(false);
      return;
    }

    // Start thumbnail fade-in immediately for faster visual return
    setShowPlaceholder(false);
    setShowDownloadButton(false);

    const finalRect = animEl.getBoundingClientRect();
    const originRect = thumbRef.current?.getBoundingClientRect() || originRectRef.current;
    const tl = gsap.timeline({
      onComplete: () => {
        setIsOpen(false);
      },
    });
    tl.to(overlay, { opacity: 0, duration: 0.2, ease: 'power2.in' }, 0);

    if (originRect) {
      const originCX = originRect.left + originRect.width / 2;
      const originCY = originRect.top + originRect.height / 2;
      const finalCX = finalRect.left + finalRect.width / 2;
      const finalCY = finalRect.top + finalRect.height / 2;
      const dx = originCX - finalCX;
      const dy = originCY - finalCY;
      const scaleX = originRect.width / finalRect.width;
      const scaleY = originRect.height / finalRect.height;

      tl.to(animEl, { x: dx, y: dy, scaleX, scaleY, duration: 0.2, ease: 'power2.in' }, 0);
    } else {
      tl.to(animEl, { opacity: 0, scale: 0.96, duration: 0.2, ease: 'power2.in' }, 0);
    }
  }, []);

  const handleDownload = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (!src) return;
      const link = document.createElement('a');
      link.href = src;
      link.download = fileName || 'check-image.jpg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    [src, fileName]
  );

  return (
    <>
      <ClickableThumb ref={thumbRef} onClick={open}>
        <HoverZoom>
          <Icon.Search />
        </HoverZoom>
        <ThumbPlaceholder $isHidden={showPlaceholder}>
          <ImageViewer src={src} />
        </ThumbPlaceholder>
      </ClickableThumb>
      {isOpen &&
        createPortal(
          <>
            <QuickLookBackdrop ref={overlayRef} onClick={handleClose}>
              <DownloadImageButton
                ref={downloadBtnRef}
                onClick={handleDownload}
                aria-label="Download check image"
                $isVisible={showDownloadButton}
              >
                <DownloadIcon />
                Download
              </DownloadImageButton>
              <QuickLookImageContainer ref={animContainerRef} onClick={(e) => e.stopPropagation()}>
                <ImageViewer src={src} onSize={() => setViewerReady(true)} />
              </QuickLookImageContainer>
            </QuickLookBackdrop>
          </>,
          document.body
        )}
    </>
  );
};
